import { useContext } from "react";
import { HiUserCircle } from "react-icons/hi";

import { TFunction } from "i18next";

import { useSwitchAccountMutation } from "graphql/mutations";
import { AccountTypeEnum } from "graphql/types";
import { AuthContext } from "providers/Authentication";
import { useTranslation } from "translations";
import { tw } from "utils/tw";

import { HeadingItem, MenuItemAction, MenuItemLink } from "./MenuList/types";

interface Props {
  hideCurrent?: boolean;
}

export default ({
  hideCurrent = false,
}: Props): (MenuItemLink | MenuItemAction | HeadingItem)[] => {
  const { switchAccount } = useSwitchAccountMutation();

  const { session, login } = useContext(AuthContext);
  const { t } = useTranslation("common");

  if (!session) return [];

  const accounts = hideCurrent
    ? session.accounts.filter(({ slug }) => slug !== session.account.slug)
    : session.accounts;

  return [
    { text: t("layout.accountMenu.title", "Change account") },
    ...accounts.map(({ id, slug, name, email, accountType }) => ({
      id: `account_menu_items-switch_account:${slug}` as const,
      Icon: HiUserCircle,
      label: getAccountMenuItemLabel({
        t,
        isCurrent: slug === session.account.slug,
        accountType,
        name: name ?? "",
        email,
      }),
      onClick: () => switchAccount({ id }, login),
    })),
  ];
};

interface LabelProps {
  t: TFunction;
  isCurrent: boolean;
  accountType: AccountTypeEnum;
  name: string;
  email: string;
}

export const getAccountMenuItemLabel = ({
  t,
  isCurrent,
  accountType,
  name,
  email,
}: LabelProps) => (
  <div
    className={tw("text-left", {
      "text-blue-900": isCurrent,
    })}
  >
    <p className={tw("text-base", "overflow-hidden", "text-ellipsis")}>
      {accountType === AccountTypeEnum.Personal
        ? t("header_loggedIn.accounts_worker.label", "Personal Account")
        : t("header_loggedIn.accounts_partner.label", "{{name}} (Connected)", {
            name,
          })}
    </p>

    <p className={tw("text-sm", "overflow-hidden", "text-ellipsis")}>{email}</p>
  </div>
);
